import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchCurrentUser } from '../../store/slices/authSlice';
import AvatarUpload from '../../components/profile/AvatarUpload';
import './Profile.css';

const Profile = () => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    theme_preference: 'light',
    timezone: '',
  });
  const { theme, changeTheme } = useTheme();

  // Password change state (local users only)
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);

  /**
   * Fetch user profile
   */
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/profile/me');
        setProfile(response.data);
        setPreferences({
          theme_preference: response.data.theme_preference || 'light',
          timezone: response.data.timezone || '',
        });
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  /**
   * Handle avatar change
   */
  const handleAvatarChange = (newAvatarUrl) => {
    setProfile((prev) => ({
      ...prev,
      avatar_url: newAvatarUrl,
    }));

    // Re-fetch current user from server to update Redux store globally
    // This ensures header, sidebar, and all components show the updated avatar
    dispatch(fetchCurrentUser());
  };

  /**
   * Handle theme change
   */
  const handleThemeChange = async (newTheme) => {
    setPreferences((prev) => ({ ...prev, theme_preference: newTheme }));
    changeTheme(newTheme);
  };

  /**
   * Handle timezone change
   */
  const handleTimezoneChange = (e) => {
    setPreferences((prev) => ({ ...prev, timezone: e.target.value }));
  };

  /**
   * Handle password change (local users only)
   */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!pwForm.current) errors.current = 'Current password is required.';

    const pwd = pwForm.next;
    const missing = [];
    if (pwd.length < 12) missing.push('at least 12 characters');
    if (!/[A-Z]/.test(pwd)) missing.push('an uppercase letter');
    if (!/[a-z]/.test(pwd)) missing.push('a lowercase letter');
    if (!/\d/.test(pwd)) missing.push('a digit');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) missing.push('a special character');
    if (missing.length) errors.next = `Password must contain ${missing.join(', ')}.`;

    if (!pwForm.confirm) errors.confirm = 'Please confirm your new password.';
    else if (pwForm.next !== pwForm.confirm) errors.confirm = 'Passwords do not match.';

    if (Object.keys(errors).length) { setPwErrors(errors); return; }

    setPwErrors({});
    setPwSubmitting(true);
    setPwSuccess(false);

    try {
      await api.post('/auth/change-password', {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      setPwForm({ current: '', next: '', confirm: '' });
      setPwSuccess(true);
      toast.success('Password changed successfully.');
    } catch (err) {
      setPwErrors({ submit: err.response?.data?.detail || 'Failed to change password.' });
    } finally {
      setPwSubmitting(false);
    }
  };

  /**
   * Save preferences
   */
  const handleSavePreferences = async () => {
    setSaving(true);

    try {
      await api.put('/profile/me', {
        timezone: preferences.timezone || null,
      });

      toast.success('Preferences saved successfully!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="profile-error">Failed to load profile</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>My Profile</h1>
        <p className="profile-subtitle">Manage your account settings and preferences</p>
      </div>

      <div className="profile-content">
        {/* Avatar Section */}
        <section className="profile-section">
          <h2>Profile Picture</h2>
          <AvatarUpload user={profile} onAvatarChange={handleAvatarChange} />
        </section>

        {/* Personal Information (Read-Only) */}
        <section className="profile-section">
          <h2>Personal Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>First Name</label>
              <div className="info-value">{profile.first_name}</div>
            </div>

            <div className="info-item">
              <label>Last Name</label>
              <div className="info-value">{profile.last_name}</div>
            </div>

            <div className="info-item">
              <label>Email</label>
              <div className="info-value">{profile.email}</div>
            </div>

            <div className="info-item">
              <label>Role</label>
              <div className="info-value role-badge">
                {profile.role.replace('_', ' ').toUpperCase()}
              </div>
            </div>

            <div className="info-item">
              <label>Sign-in Method</label>
              <div className="info-value">
                {profile.auth_provider === 'local' ? (
                  <span className="auth-method-badge auth-method-local">Email &amp; Password</span>
                ) : (
                  <span className="auth-method-badge auth-method-azure">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="14" height="14" aria-hidden="true" style={{ flexShrink: 0 }}>
                      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                    </svg>
                    Microsoft (Azure SSO)
                  </span>
                )}
              </div>
            </div>

            <div className="info-item">
              <label>Account Created</label>
              <div className="info-value">{formatDate(profile.created_at)}</div>
            </div>

            <div className="info-item">
              <label>Last Login</label>
              <div className="info-value">{formatDate(profile.last_login)}</div>
            </div>
          </div>
          <p className="info-note">
            To update your name or email, please contact your administrator.
          </p>
        </section>

        {/* Password Change (local users only) */}
        {profile.auth_provider === 'local' && (
          <section className="profile-section">
            <h2>Change Password</h2>
            {pwErrors.submit && (
              <div className="form-error" style={{ marginBottom: 'var(--space-4)' }}>{pwErrors.submit}</div>
            )}
            <form onSubmit={handleChangePassword} noValidate>
              <div className="form-group">
                <label className="form-label" htmlFor="pw-current">Current Password</label>
                <input
                  id="pw-current"
                  type="password"
                  className={`form-input${pwErrors.current ? ' error' : ''}`}
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={pwSubmitting}
                />
                {pwErrors.current && <span className="field-error">{pwErrors.current}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="pw-new">New Password</label>
                <input
                  id="pw-new"
                  type="password"
                  className={`form-input${pwErrors.next ? ' error' : ''}`}
                  value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  placeholder="Create a new password"
                  autoComplete="new-password"
                  disabled={pwSubmitting}
                />
                {pwErrors.next && <span className="field-error">{pwErrors.next}</span>}
              </div>

              {pwForm.next.length > 0 && (
                <div className="password-requirements">
                  <p className="requirements-title">Requirements</p>
                  <ul>
                    <li className={pwForm.next.length >= 12 ? 'met' : ''}>At least 12 characters</li>
                    <li className={/[A-Z]/.test(pwForm.next) ? 'met' : ''}>At least one uppercase letter</li>
                    <li className={/[a-z]/.test(pwForm.next) ? 'met' : ''}>At least one lowercase letter</li>
                    <li className={/\d/.test(pwForm.next) ? 'met' : ''}>At least one digit</li>
                    <li className={/[!@#$%^&*(),.?":{}|<>]/.test(pwForm.next) ? 'met' : ''}>At least one special character</li>
                  </ul>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="pw-confirm">Confirm New Password</label>
                <input
                  id="pw-confirm"
                  type="password"
                  className={`form-input${pwErrors.confirm ? ' error' : ''}`}
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  disabled={pwSubmitting}
                />
                {pwErrors.confirm && <span className="field-error">{pwErrors.confirm}</span>}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={pwSubmitting}
              >
                {pwSubmitting ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </section>
        )}

        {/* Preferences */}
        <section className="profile-section">
          <h2>Preferences</h2>

          <div className="preferences-form">
            {/* Theme Selection */}
            <div className="form-group">
              <label>Theme</label>
              <div className="theme-selector">
                <button
                  type="button"
                  className={`theme-option ${
                    preferences.theme_preference === 'light' ? 'active' : ''
                  }`}
                  onClick={() => handleThemeChange('light')}
                >
                  <span className="theme-icon">☀️</span>
                  <span>Light</span>
                </button>

                <button
                  type="button"
                  className={`theme-option ${
                    preferences.theme_preference === 'dark' ? 'active' : ''
                  }`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <span className="theme-icon">🌙</span>
                  <span>Dark</span>
                </button>

                <button
                  type="button"
                  className={`theme-option ${
                    preferences.theme_preference === 'system' ? 'active' : ''
                  }`}
                  onClick={() => handleThemeChange('system')}
                >
                  <span className="theme-icon">💻</span>
                  <span>System</span>
                </button>
              </div>
            </div>

            {/* Timezone Selection (Optional) */}
            <div className="form-group">
              <label htmlFor="timezone">Timezone (Optional)</label>
              <select
                id="timezone"
                style = {{ marginBottom: '0px', paddingBottom: '0px', paddingTop: '0px' }}
                value={preferences.timezone}
                onChange={handleTimezoneChange}
              >
                <option value="">-- Select Timezone --</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="UTC">UTC</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Australia/Sydney">Sydney (AEDT)</option>
              </select>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSavePreferences}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Profile;
